
/**
 * Module dependencies.
 */

import { GetServerSidePropsContext } from 'next';
import { UserSession, UserType } from 'src/types/user';
import { authRoutes, myRevigresRoutes } from 'src/core/routes';
import { destroyCookie, parseCookies } from 'nookies';
import { fetchUser as fetchAuthUser } from 'src/api/auth/fetch-user';
import { fetchUser } from 'src/api/app/user/fetch-user';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';

/**
 * Export `serverAuth`.
 */

export async function serverAuth(ctx) {
  // Get cookies.
  const cookies = parseCookies(ctx);

  // Get token.
  let token = cookies?.token;

  // Get auth user.
  const userAuthData = await fetchAuthUser({ ssr: true, token });

  if (!userAuthData) {
    destroyCookie(ctx, 'token');
    token = null;
  }

  // Get user.
  const userData = await fetchUser({ ssr: true, token });

  return {
    authUser: userAuthData,
    isAuthenticated: !isEmpty(userAuthData),
    isSignUpCompleted: !isEmpty(userAuthData) && !isEmpty(userData),
    token,
    user: userData
  };
}

/**
 * Export `isAccessGranted`.
 */

export function isAccessGranted(permissions: UserType[], userType: UserType): boolean {
  return includes(permissions, userType);
}

/**
 * Export `withSession`
 */

export function withSession(session?: UserSession) {
  return async (ctx: GetServerSidePropsContext) => {
    // TODO: Remove this bypass after accessibility testing
    return { props: {} };

    const { isAuthenticated, isSignUpCompleted, user } = await serverAuth(ctx);

    if (!isAuthenticated) {
      return {
        redirect: {
          destination: authRoutes.signIn,
          permanent: false
        }
      };
    }

    if (isAuthenticated && !isSignUpCompleted) {
      return {
        redirect: {
          destination: myRevigresRoutes.signUpComplete,
          permanent: false
        }
      };
    }

    const userTypePermission = session?.userTypePermission;
    const userType = user?.type;

    if (userTypePermission && userType && !isAccessGranted(userTypePermission, userType)) {
      return {
        redirect: {
          destination: myRevigresRoutes.home,
          permanent: false
        }
      };
    }

    return {
      props: {}
    };
  };
}
